import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ToastService } from '../../../base/services/toast.service';
import { SpinnerService } from '../../../base/spinner/spinner.service';
import {
  SubscriptionClient,
  SubscriptionDTO,
  CreateSubscriptionDTO,
  UpdateSubscriptionDTO,
  PagedResultDTOOfUserInfoDTO, FilterDTO, SortDirection,

} from '../../../base/models/models.swagger.generated';
import { ErrorType } from '../../../core/routing/error-types';
import { IPagedItems } from 'src/app/base/shared/table/paged-items.interface';
import { PagedQueryModel } from '../../../base/shared/table/paged-query-model';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  constructor(
    private subscriptionClient: SubscriptionClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService


  ) { }

  public getData(pagedQuery: PagedQueryModel): Observable<IPagedItems> {
    this.spinnerService.showSpinner = true;
    return this.subscriptionClient
      .searchAllSubscription(
        pagedQuery.filters,
        pagedQuery.skip,
        pagedQuery.take,
        pagedQuery.sortBy,
        pagedQuery.sortDirection
      )
      .pipe(
        tap(() => {
          this.spinnerService.showSpinner = false;
        }),
        catchError((error: any) => {
          this.spinnerService.showSpinner = false;
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError(
              'i18n.errors.server-error.title',
              'i18n.errors.server-error.message',
              null,
              ErrorType.serverError
            );
          }
          return throwError(() => error);
        })
      );
  }

  public updateRecord(data: UpdateSubscriptionDTO): Observable<SubscriptionDTO> {
    this.spinnerService.showSpinner = true;
    return this.subscriptionClient.updateSubscription(data).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.subscription.messages.update-title',
          'i18n.domain.subscription.messages.update-success-message'
        );
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public addRecord(data: CreateSubscriptionDTO): Observable<SubscriptionDTO> {
    this.spinnerService.showSpinner = true;
    return this.subscriptionClient.createSubscription(data).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.subscription.messages.add-title',
          'i18n.domain.subscription.messages.add-success-message'
        );
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public deleteRecord(id: number): Observable<any> {
    this.spinnerService.showSpinner = true;
    return this.subscriptionClient.deleteSubscription(id).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.subscription.messages.delete-title',
          'i18n.domain.subscription.messages.delete-success-message'
        );
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;

        // Parse the response if it's a JSON string to extract custom error message
        let parsedResponse: any = null;
        if (error?.response && typeof error.response === 'string') {
          try {
            parsedResponse = JSON.parse(error.response);
          } catch (e) {
            console.error('Failed to parse error response:', e);
          }
        }

        // Extract custom message from parsed response or error object
        const customMessage =
          parsedResponse?.message ||
          error?.error?.message ||
          error?.message;

        // Show custom error message if available, otherwise show generic error
        if (customMessage && typeof customMessage === 'string' && customMessage.trim().length > 0) {
          this.toastService.showError('Cannot Delete Subscription', customMessage);
        } else if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }

        return throwError(() => error);
      })
    );
  }

  public searchUserForSubscription(
    subscriptionId: number,
    pagedQuery: PagedQueryModel
  ): Observable<PagedResultDTOOfUserInfoDTO> {
    this.spinnerService.showSpinner = true;
    return this.subscriptionClient
      .searchUserForSubscription(
        subscriptionId,
        pagedQuery.filters,
        pagedQuery.skip,
        pagedQuery.take,
        pagedQuery.sortBy,
        pagedQuery.sortDirection
      )
      .pipe(
        tap(() => {
          this.spinnerService.showSpinner = false;
        }),
        catchError((error: any) => {
          this.spinnerService.showSpinner = false;
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError(
              'i18n.errors.server-error.title',
              'i18n.errors.server-error.message',
              null,
              ErrorType.serverError
            );
          }
          return throwError(() => error);
        })
      );
  }

}
